// Qué le dice el cuestionario de onboarding a una cuenta que YA PAGÓ —
// ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago.
//
// El cuestionario (`/configuracion-cuenta`: 4 pasos + generando + resultados) se escribió
// para una cuenta nueva en PRUEBA GRATIS, que hasta ahora era la única que llegaba: se
// entraba por `/auth`, se respondía, y recién al final se ofrecía un plan. Desde que el
// alta por link se invirtió (ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-
// despues) también lo hace alguien que ACABA DE PAGAR, y ahí las tres cosas que le
// decimos están mal:
//
//   1. el paso del producto lo invita a "probar Estudio Atlas gratis";
//   2. la pantalla de resultados le cuenta 10 créditos gratuitos (tiene los de su plan);
//   3. al salir se le abre el catálogo, a venderle el plan que compró hace tres minutos.
//
// LA CONDICIÓN ES "ESTA EMPRESA TIENE PLAN PAGO", no "vino del alta por link". El camino
// por el que llegó es un accidente de hoy —mañana puede mandar al cuestionario cualquier
// otra pantalla— y lo que hace que el copy esté mal es el plan. Es la misma pregunta que
// hace el resto de la app: `isPaidPlan`.
//
// PLAN DESCONOCIDO = SE LE HABLA COMO A UNA CUENTA GRATIS. Es al revés de la convención de
// los gates de features (allá "no sé" cuenta como gratis para no repartir accesos de más).
// Acá no se reparte acceso a nada: lo único que se decide es QUÉ TEXTO se muestra, y el
// requisito del cambio es que la cuenta gratis —el 99% de las llegadas— vea EXACTAMENTE lo
// mismo que antes aunque la consulta del plan falle o llegue tarde. El costo del error es
// asimétrico y está aceptado: si el plan no llegó a tiempo, un cliente pago puede ver por
// un instante el copy de la prueba gratis; nunca al revés.
//
// Vive en un módulo puro y no adentro de las pantallas por dos razones: los tests de este
// repo son de lógica pura (no hay DOM, un test de componente no se puede escribir), y el
// mismo criterio lo necesitan TRES lugares en dos pantallas distintas — sueltos, se iban a
// desincronizar.

import { isPaidPlan } from './planCapabilities';

/** El título del paso del producto, tal cual lo vio siempre la prueba gratis. */
const TITULO_PRUEBA_GRATIS = 'Todo listo, subí tu producto y probá Estudio Atlas gratis';
/** Para el que ya pagó: la misma invitación a subir el producto, sin la promesa. */
const TITULO_PLAN_PAGO = 'Todo listo, subí tu producto y creá tu primera imagen';

/** La nota de créditos de resultados, tal cual la vio siempre la prueba gratis. */
const CREDITOS_PRUEBA_GRATIS = 'Tenés 10 créditos gratuitos esperándote';
/**
 * Para el que ya pagó. Afirma que el plan está activo, y puede afirmarlo: este texto sólo
 * aparece cuando `company_profiles.plan` YA dice un plan pago, o sea cuando el webhook del
 * cobro terminó de correr — que es el mismo momento en que le acreditó los créditos.
 */
const CREDITOS_PLAN_PAGO = 'Tu plan ya está activo, con todos tus créditos';

/** Título del paso 4 (subir el producto). */
export function tituloDelPasoDelProducto(plan: string | null | undefined): string {
  return isPaidPlan(plan) ? TITULO_PLAN_PAGO : TITULO_PRUEBA_GRATIS;
}

/** Nota de créditos de la pantalla de resultados (mobile y desktop dicen lo mismo). */
export function notaDeCreditos(plan: string | null | undefined): string {
  return isPaidPlan(plan) ? CREDITOS_PLAN_PAGO : CREDITOS_PRUEBA_GRATIS;
}

/**
 * ¿Al tocar "Seguir explorando Atlas" se abre el catálogo de planes?
 *
 * Ya no: descargar una de las primeras imágenes abría el catálogo
 * (ADR-2026-07-29-upsell-planes-salir-alta-condicionado) y eso se sacó
 * (ADR-2026-10-31-descargar-no-abre-los-planes). Sólo queda la preview de desarrollo.
 */
export function abreElCatalogoDePlanes(p: {
  /** `/onboarding-pricing-dev`, detrás de `import.meta.env.DEV`: existe justamente para
   *  poder MIRAR el catálogo sin reproducir el alta entera, así que ahí se abre siempre. */
  previewDeDesarrollo?: boolean;
}): boolean {
  return p.previewDeDesarrollo === true;
}
